import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useActiveUploads, useAuthState, useTutorialsActions, useTutorialsState } from "@/store";
import { getCurrentAuthUser } from "@/Service/authService";
import { TUTORIALS } from "./registry";
import { decideTutorialPrompt, TUTORIAL_STATUS } from "./decideTutorialPrompt";
import { rememberPromptShownThisSession, wasPromptShownThisSession } from "./tutorialStateStorage";
import { TutorialPromptDialog } from "./TutorialPromptDialog";
import { useStartTutorial } from "./useStartTutorial";
import { trackTutorialEvent } from "./tutorialAnalytics";
import { isTourActive, stopActiveTour } from "./tourEngine";

// Small delay so the prompt appears after the page has settled (mirrors
// IosAppPrompt.APPEAR_DELAY_MS) instead of flashing over the loader.
const APPEAR_DELAY_MS = 1500;
const RETRY_DELAY_MS = 5000;

/**
 * Mounted once in App.js (only when signed in). Loads the user's tutorial
 * state, asks decideTutorialPrompt whether a new/updated tutorial should be
 * offered, and shows the dialog at most once per tab session.
 *
 * "Once after a deploy" falls out naturally: a deploy ships a registry with a
 * new tutorial (or a bumped version); VersionUpdateManager reloads the page;
 * this manager runs on boot and finds a tutorial the user hasn't seen.
 */
export const TutorialPromptManager = () => {
  const { currentUser, fetch } = useAuthState();
  const activeUploads = useActiveUploads();
  const tutorialsState = useTutorialsState();
  const { fetchTutorialState, fetchTutorialsConfig, markTutorial, setSuppressPrompts } = useTutorialsActions();
  const startTutorial = useStartTutorial();
  const location = useLocation();

  const [candidate, setCandidate] = useState(null);
  const [open, setOpen] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const shownRef = useRef(false);

  const uid = getCurrentAuthUser()?.uid || null;
  const roleLoaded = fetch?.status === "succeeded" && !!currentUser;

  // This manager is mounted only while someone is signed in (App.js). Signing
  // out unmounts it — end any tour still running over the vanished screens.
  useEffect(() => () => stopActiveTour(), []);

  // A different person signed in (same tab): forget that we already prompted.
  const promptedForUidRef = useRef(null);
  useEffect(() => {
    if (uid && promptedForUidRef.current && promptedForUidRef.current !== uid) {
      shownRef.current = false;
      setCandidate(null);
      setOpen(false);
    }
    if (uid) promptedForUidRef.current = uid;
  }, [uid]);

  // Load persisted state once per signed-in uid.
  useEffect(() => {
    if (!uid || !roleLoaded) return;
    if (!tutorialsState.configLoaded) fetchTutorialsConfig();
    if (tutorialsState.uid === uid && tutorialsState.loaded) return;
    fetchTutorialState(uid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, roleLoaded]);

  const decision = useMemo(
    () =>
      decideTutorialPrompt({
        tutorials: TUTORIALS,
        userState: tutorialsState,
        stateLoaded: roleLoaded && tutorialsState.loaded && tutorialsState.uid === uid && tutorialsState.configLoaded,
        config: tutorialsState.config,
        sessionAlreadyShown: shownRef.current || wasPromptShownThisSession(uid),
        uploadsInFlight: activeUploads.length,
        pathname: location.pathname,
        role: currentUser?.role,
      }),
    [tutorialsState, roleLoaded, uid, activeUploads.length, location.pathname, currentUser?.role]
  );

  useEffect(() => {
    if (!decision || open || shownRef.current) return undefined;
    const handle = window.setTimeout(() => {
      // Re-check the cheap runtime guards at fire time: a tour may have been
      // started from the page meanwhile, or the update-required modal may be up.
      // Blocked → try again a little later instead of silently dropping it.
      if (isTourActive() || document.querySelector('[role="alertdialog"]')) {
        setRetryTick((n) => n + 1);
        return;
      }
      shownRef.current = true;
      rememberPromptShownThisSession(uid);
      setCandidate(decision);
      setOpen(true);
      trackTutorialEvent("tutorial_prompt_shown", { tutorialId: decision.id, version: decision.version });
    }, retryTick > 0 ? RETRY_DELAY_MS : APPEAR_DELAY_MS);
    return () => window.clearTimeout(handle);
  }, [decision, open, retryTick, uid]);

  const applySuppress = useCallback(
    (suppress) => {
      if (suppress) {
        setSuppressPrompts(true);
        trackTutorialEvent("tutorial_suppressed", { tutorialId: candidate?.id });
      }
    },
    [candidate, setSuppressPrompts]
  );

  const handleSkip = useCallback(
    async ({ suppress } = {}) => {
      setOpen(false);
      if (!candidate) return;
      trackTutorialEvent("tutorial_prompt_skipped", { tutorialId: candidate.id, version: candidate.version, persisted: !!suppress });
      // "Not now" writes NOTHING: the session marker already stops a re-show
      // this session, and the next sign-in offers the tutorial again. Only the
      // "don't show again" checkbox records SKIPPED (and silences future tips)
      // — before this, a click on the backdrop skipped the tutorial forever.
      if (suppress) {
        // Sequential: both thunks snapshot + rewrite the whole persisted
        // object, so a concurrent pair would clobber each other.
        await markTutorial({ tutorialId: candidate.id, status: TUTORIAL_STATUS.SKIPPED, version: candidate.version });
        applySuppress(true);
      }
    },
    [candidate, markTutorial, applySuppress]
  );

  const handleStart = useCallback(
    ({ suppress } = {}) => {
      setOpen(false);
      applySuppress(suppress);
      if (candidate) startTutorial(candidate, { source: "prompt" });
    },
    [candidate, applySuppress, startTutorial]
  );

  return (
    <TutorialPromptDialog
      open={open}
      tutorial={candidate}
      mandatory={!!candidate && (tutorialsState.config?.mandatory || []).includes(candidate.id)}
      onStart={handleStart}
      onSkip={handleSkip}
    />
  );
};
